import i18nFactory from '@yandex-int/i18n';
import { Link } from 'lego-on-react';
import * as React from 'react';

import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { CheckboxDecorator } from '../../components/CheckboxDecorator/CheckboxDecorator';
import { ModalDecorator } from '../../components/ModalDecorator/ModalDecorator';
import { Shape2, Shape2Icon } from '../../components/Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../../components/StyledLink/StyledLink';
import { Bem } from '../../service/Bem/Bem';
import { copyTextToClipboard } from '../../service/Clipboard/Clipboard';
import { CommonCreativeBaseItem } from '../AddCreativePage/typings';
import { PcodeCanvasPreview } from '../PcodeCanvasPreview/PcodeCanvasPreview';
import { PcodeStaticCanvasPreview } from '../PcodeStaticCanvasPreview/PcodeStaticCanvasPreview';

import './CommonCreativesSelector.css';
import * as keyset from './CommonCreativesSelector.i18n';

const i18n = i18nFactory(keyset);
const BLOCK = 'common-creatives-selector';

interface CreativePreviewProps {
    item: CommonCreativeBaseItem;
    toggle?: (id: number, val: boolean) => void;
    link?: boolean;
    crop?: (id: number) => void;
    controls?: boolean;
    sizeFilter?: boolean;
    showId?: boolean;
}

class CreativePreview extends React.PureComponent<CreativePreviewProps> {
    private previewRef: React.RefObject<PcodeCanvasPreview> = React.createRef();

    componentDidUpdate(prevProps: CreativePreviewProps): void {
        if (
            this.previewRef.current &&
            this.props.item.pcodeData &&
            prevProps.item.pcodeData !== this.props.item.pcodeData
        ) {
            this.previewRef.current.onExternalDataReceived(this.props.item.pcodeData, true);
        }
    }

    render() {
        const { item, toggle, link, crop, controls, showId, sizeFilter } = this.props;
        const size = i18n('item-size', { width: item.data.width, height: item.data.height });
        const itemId = i18n('item-id', { id: item.id });
        const fitsSizeFilter = sizeFilter ? item.fitsSizeFilter : true;

        return (
            <div className={Bem.cls(BLOCK, 'item', { toggle: Boolean(toggle) })}>
                {toggle && fitsSizeFilter && (
                    <CheckboxDecorator
                        cls={Bem.cls(BLOCK, 'item-checkbox')}
                        theme={'normal'}
                        checked={item.selected}
                        size={'m'}
                        item={item}
                        onChecked={this.onChecked}
                    />
                )}
                <div
                    className={Bem.cls(BLOCK, 'preview', {
                        selected: crop || !controls || (item.selected && fitsSizeFilter) ? 'yes' : 'no'
                    })}
                >
                    {this.renderPreview()}
                </div>
                {controls && (
                    <div className={Bem.cls(BLOCK, 'item-controls')}>
                        <span className={Bem.cls(BLOCK, 'item-size')}>{size}</span>
                        {showId && <span className={Bem.cls(BLOCK, 'item-id')}>{itemId}</span>}
                        {link && item.previewURL && (
                            <Link
                                title={i18n('copy')}
                                theme={'ghost'}
                                pseudo={true}
                                cls={Bem.cls(BLOCK, 'item-link')}
                                onClick={this.copyLink}
                            >
                                <Shape2
                                    cls={Bem.cls(BLOCK, 'link-icon')}
                                    icon={Shape2Icon.link2}
                                    width={16}
                                    height={16}
                                />
                            </Link>
                        )}
                        {crop && (
                            <StyledLink
                                theme={StyledLinkTheme.black}
                                cls={Bem.cls(BLOCK, 'crop-link')}
                                onClick={this.onCrop}
                            >
                                <Shape2
                                    cls={Bem.cls(BLOCK, 'crop-link-icon')}
                                    icon={Shape2Icon.crop}
                                    width={16}
                                    height={16}
                                />
                                {i18n('crop')}
                            </StyledLink>
                        )}
                    </div>
                )}
            </div>
        );
    }

    private renderPreview = (): React.ReactNode => {
        const { item } = this.props;

        if (item.pcodeData) {
            return (
                <PcodeCanvasPreview
                    key={item.id}
                    ref={this.previewRef}
                    pcodeData={item.pcodeData}
                    currentSize={{
                        width: item.data.width,
                        height: item.data.height
                    }}
                />
            );
        }

        if (item.creativeURL) {
            return (
                <PcodeStaticCanvasPreview
                    key={item.id}
                    previewUrl={item.creativeURL}
                    currentSize={{
                        width: item.data.width,
                        height: item.data.height
                    }}
                />
            );
        }

        return null;
    };

    private onCrop = (): void => {
        const { crop, item } = this.props;
        if (crop) {
            crop(item.id);
        }
    };

    private onChecked = (e: React.KeyboardEvent<HTMLElement>, data: { checked: boolean }) => {
        if (this.props.toggle) {
            this.props.toggle(this.props.item.id, data.checked);
        }
    };

    private copyLink = () => {
        if (this.props.item.previewURL) {
            copyTextToClipboard(this.props.item.previewURL);
        }
    };
}

function fitBySizeItems(dp: CommonCreativeBaseItem[]): CommonCreativeBaseItem[] {
    return dp.filter(item => item.fitsSizeFilter);
}

export interface CommonCreativesSelectorProps {
    cls?: string;
    header?: string;
    items: CommonCreativeBaseItem[];
    toggle?: (ids: number[], val: boolean) => void;
    submit: () => void;
    cancel: () => void;
    close: () => void;
    cancelText?: string;
    submitText?: string;
    link?: boolean;
    crop?: (id: number) => void;
    previewControls?: boolean;
    cancelEnabled?: boolean;
    sizeFilter?: boolean;
    showId?: boolean;
    additionalCancel?: () => void;
    additionalCancelText?: string;
}

export class CommonCreativesSelector extends React.PureComponent<CommonCreativesSelectorProps> {
    static defaultProps = {
        cancelText: i18n('cancel'),
        submitText: i18n('submit'),
        link: true,
        previewControls: true,
        sizeFilter: true,
        cancelEnabled: true,
        showId: true
    };

    render() {
        const {
            items,
            cancelEnabled,
            header,
            cancel,
            toggle,
            cancelText,
            submitText,
            link,
            crop,
            previewControls,
            sizeFilter,
            close,
            cls,
            additionalCancel,
            additionalCancelText,
            showId
        } = this.props;

        const sortedItems = items.concat().sort((item1, item2): number => {
            return item1.fitsSizeFilter === item2.fitsSizeFilter ? 0 : item1.fitsSizeFilter ? -1 : 1;
        });
        const filteredItems = sizeFilter ? fitBySizeItems(items) : items;
        const filteredTotal = filteredItems.length;
        const allChecked = Boolean(filteredTotal && filteredItems.every(item => item.selected));
        const batchName = items[0].name;

        return (
            <ModalDecorator
                theme={'clear'}
                hasClose={true}
                header={header || i18n('title', { creativeName: batchName })}
                visible={true}
                onClose={close}
                cls={cls}
                footer={
                    <>
                        <ButtonDecorator
                            cls={Bem.cls(BLOCK, 'footer-action')}
                            theme="action"
                            size="n"
                            text={submitText}
                            disabled={!this.getSubmitEnabled(allChecked, filteredItems)}
                            onClick={this.submit}
                        />
                        {additionalCancel && additionalCancelText && (
                            <ButtonDecorator
                                cls={Bem.cls(BLOCK, 'footer-action')}
                                theme="normal"
                                size="n"
                                text={additionalCancelText}
                                onClick={additionalCancel}
                            />
                        )}
                        <ButtonDecorator
                            cls={Bem.cls(BLOCK, 'footer-action')}
                            theme="normal"
                            size="n"
                            text={cancelText}
                            onClick={cancel}
                            disabled={!cancelEnabled}
                        />
                    </>
                }
            >
                <div className={Bem.cls(BLOCK, 'scrollable')}>
                    {toggle && filteredTotal > 0 && (
                        <CheckboxDecorator
                            theme={'normal'}
                            text={this.getAllCheckboxLabel(items.length, filteredItems.length)}
                            size={'m'}
                            cls={Bem.cls(BLOCK, 'all-checkbox')}
                            onChecked={this.onAllCheckedChange}
                            checked={allChecked}
                        />
                    )}
                    {sortedItems.map(item => {
                        return (
                            <CreativePreview
                                key={item.id}
                                item={item}
                                link={link}
                                toggle={toggle ? this.onToggle : undefined}
                                crop={crop}
                                controls={previewControls}
                                showId={showId}
                                sizeFilter={sizeFilter}
                            />
                        );
                    })}
                </div>
            </ModalDecorator>
        );
    }

    private submit = (): void => {
        this.props.submit();
    };

    private getAllCheckboxLabel = (total: number, filteredTotal: number): string => {
        let allCheckboxLabel = i18n('check-all', { total: filteredTotal });

        if (filteredTotal !== total) {
            const filteredOut = total - filteredTotal;
            allCheckboxLabel += '. ' + i18n('filtered-items', { filteredOut });
        }

        return allCheckboxLabel;
    };

    private getSubmitEnabled = (allChecked: boolean, filteredItems: CommonCreativeBaseItem[]): boolean => {
        const { toggle } = this.props;

        if (!toggle) {
            return true;
        }

        return allChecked || filteredItems.some(item => item.selected);
    };

    private onAllCheckedChange = (e: React.KeyboardEvent<HTMLElement>, data: { checked: boolean }) => {
        if (this.props.toggle) {
            const ids = fitBySizeItems(this.props.items).map(item => item.id);
            this.props.toggle(ids, data.checked);
        }
    };

    private onToggle = (id: number, val: boolean) => {
        if (this.props.toggle) {
            this.props.toggle([id], val);
        }
    };
}
